import {Injectable, OnDestroy} from '@angular/core';
import {WertelisteElement, Wertemenge} from '../models/uiArtefakt';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, of, Subject, Subscription} from 'rxjs';
import {catchError, debounceTime, map, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {EventService} from './event.service';
import {ErrorService} from '@lv1871/core';
import {Bereitstellung} from '../models/bereitstellung';
import {UiConfigService} from '@lv1871/core';
import {SanitizationService} from './sanitization.service';

const EMPTY_WERTELISTE_ELEMENT = {
  WertEingabe: null,
  WertAnzeige: ''
} as WertelisteElement;

@Injectable()
export class WertemengenService implements OnDestroy {

  public filteredOptions$ = new BehaviorSubject<WertelisteElement[]>([]);
  private _filterRequest$ = new Subject<{ uiArtefakt: Wertemenge; suchstring: string; blacklistValues: string[]; maxResults?: number }>();
  private _subscription = Subscription.EMPTY;

  constructor(private _http: HttpClient,
              private _uiConfigService: UiConfigService,
              private _eventService: EventService,
              private _errorService: ErrorService,
              private _sanitizer: SanitizationService) {
    this._subscription = this._filterRequest$.pipe(
        withLatestFrom(this._eventService.uiControls$),
        debounceTime(300),
        switchMap(([filterRequest, uiControl]) => this._http.post<WertelisteElement[]>(
            this._buildUrl(filterRequest.uiArtefakt.Identifikator), {
              suchstring: filterRequest.suchstring,
              minLength: 0,
              blacklistValues: filterRequest.blacklistValues,
              maxResults: this._bestimmeMaxResult(filterRequest.suchstring, filterRequest.maxResults),
              uiVorgang: this._sanitizer.sanitize(uiControl).uiVorgang,
              produktId: uiControl.produktId,
              antragsgenerationDatum: uiControl.antragsgenerationDatum
            }).pipe(catchError((error) => {
              const errorMessage = 'Fehler beim Laden der Werteliste für ' + filterRequest.uiArtefakt.Identifikator + ': ';
              if (typeof (error.error) === 'string') {
                this._errorService.pushErrorMessage(errorMessage + error.error);
              } else {
                this._errorService.pushErrorMessage(errorMessage + error.message);
              }
              return of(new Array<WertelisteElement>());
            }),
            map(options => {
              if (filterRequest.uiArtefakt.WertelisteMitNullWertJN) {
                return [EMPTY_WERTELISTE_ELEMENT, ...options] as WertelisteElement[];
              }
              return options;
            }))))
    .subscribe(this.filteredOptions$);
  }

  public filterWertemenge(uiArtefakt: Wertemenge, suchstring: string, blacklistValues?: string[], maxResults?: number): boolean {
    if (uiArtefakt.EnumBereitstellung === Bereitstellung.SYNCHRON) {
      if (!uiArtefakt.Werteliste || uiArtefakt.Werteliste.length === 0) {
        this.filteredOptions$.next([]);
        this._errorService.pushWarningMessage('Das Artefakt ' + uiArtefakt.Identifikator +
            ' ist mit EnumBereitstellung=Synchron konfiguriert, aber die mitgelieferte Werteliste ist leer');
      } else if (!suchstring) {
        if (uiArtefakt.WertelisteMitNullWertJN) {
          this.filteredOptions$.next([EMPTY_WERTELISTE_ELEMENT, ...uiArtefakt.Werteliste] as WertelisteElement[]);
        } else {
          this.filteredOptions$.next(uiArtefakt.Werteliste);
        }
      } else {
        const compareString = suchstring.toLowerCase();
        const filterList = uiArtefakt.Werteliste.filter(option => option.WertAnzeige.toLowerCase().indexOf(compareString) > -1);

        filterList.sort((elemA, elemB) => {
          if (elemA.WertAnzeige.toLowerCase().startsWith(compareString) &&
              !elemB.WertAnzeige.toLowerCase().startsWith(compareString)) {
            return -1;
          } else if (!elemA.WertAnzeige.toLowerCase().startsWith(compareString) &&
              elemB.WertAnzeige.toLowerCase().startsWith(compareString)) {
            return 1;
          } else {
            return elemA.WertAnzeige.localeCompare(elemB.WertAnzeige);
          }
        });
        this.filteredOptions$.next(filterList);
      }
      return false; // synchrone Suche
    } else {
      this._filterRequest$.next({uiArtefakt, suchstring, blacklistValues, maxResults});
      return true; // asynchrone Suche
    }
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private _buildUrl(artefaktIdentifikator: string): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/aufzaehlung/' + artefaktIdentifikator;
  }

  private _bestimmeMaxResult(suchstring: string, filterMaxResult: number): number {
    const subSearch = suchstring.substring(0,5).toLowerCase();
    return (subSearch === 'stude' || subSearch === 'indus') ? 190 : (filterMaxResult ? filterMaxResult : 30);
  }
}
